import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Copy, Play, Film, Flame, Quote, Scissors, Sparkles, Palette, Share2, Loader2, Eye, Heart, ThumbsUp, X } from "lucide-react";
import { mediaUrl, trimClip, restyleClip, reframeLock, setRoster, setPreferred, shareUrl, rateClip } from "../lib/api";
import { fmt, scoreColor } from "../lib/format";
import { WaveformTrimmer } from "./WaveformTrimmer";
import { StyleControls } from "./StyleControls";
import { PublishHub } from "./PublishHub";
import { toast } from "sonner";

export const ClipCard = ({ clip, show, index, onUpdated, sourceLabel }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [panel, setPanel] = useState(null); // 'trim' | 'style' | 'publish'
  const [saving, setSaving] = useState(false);
  const [rating, setRating] = useState(clip.rating || null);

  const rate = async (r) => {
    const next = rating === r ? "none" : r;
    setRating(next === "none" ? null : next);
    try {
      await rateClip(clip.id, next);
      if (next === "kill") { toast("Killed — we'll suggest fewer like this", { icon: "🗑" }); onUpdated?.(); }
      else if (next !== "none") toast.success(next === "love" ? "Loved — more of this energy coming" : "Kept — noted for your Style DNA");
    } catch {
      toast.error("Could not save feedback");
    }
  };

  const toggle = (p) => setPanel((cur) => (cur === p ? null : p));

  const copyCaption = () => {
    const tags = (clip.hashtags || []).join(" ");
    navigator.clipboard.writeText(`${clip.caption}\n\n${tags}`.trim());
    toast.success("Caption copied for posting");
  };

  const download = (path, ext) => {
    const a = document.createElement("a");
    a.href = mediaUrl(path);
    a.download = `${clip.title.replace(/[^a-z0-9]+/gi, "_")}.${ext}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const saveTrim = async (start, end) => {
    setSaving(true);
    try {
      await trimClip(clip.id, { start, end });
      toast.success("Clip re-cut & video regenerated");
      setPanel(null); setShowVideo(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Trim failed");
    } finally { setSaving(false); }
  };

  const pad = async (side, secs) => {
    if (saving) return;
    const dur = show?.duration_sec || (clip.end_sec + 300);
    let start = clip.start_sec, end = clip.end_sec;
    if (side === "start") start = Math.max(0, +(start - secs).toFixed(2));
    else end = Math.min(dur, +(end + secs).toFixed(2));
    if (Math.abs(start - clip.start_sec) < 0.05 && Math.abs(end - clip.end_sec) < 0.05) {
      toast.info(side === "start" ? "Already at the start of the show" : "Already at the end of the show");
      return;
    }
    setSaving(true);
    try {
      await trimClip(clip.id, { start, end });
      toast.success(`Added ${secs}s of ${side === "start" ? "lead-in" : "runway"} — captions & reframe regenerated`);
      setShowVideo(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Expand failed");
    } finally { setSaving(false); }
  };

  const applyStyle = async (style) => {
    setSaving(true);
    try {
      await restyleClip(clip.id, style);
      toast.success("Caption style applied — video re-rendered");
      setPanel(null); setShowVideo(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restyle failed");
    } finally { setSaving(false); }
  };

  const lockReframe = async (tileIdx) => {
    setSaving(true);
    try {
      await reframeLock(clip.id, tileIdx);
      toast.success("Crop locked on selected face — video re-rendered");
      setPanel(null); setShowVideo(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not lock on that face");
    } finally { setSaving(false); }
  };

  const nameFaces = async ({ faces, prefer, makeGlobal }) => {
    setSaving(true);
    try {
      await setRoster(show.id, faces);
      if (prefer) {
        await setPreferred(show.id, { ...prefer, apply_all: true, global: !!makeGlobal });
        toast.success(`Saved names — following ${prefer.name || "that person"} across every clip${makeGlobal ? " (and future shows)" : ""} (re-rendering)`);
      } else {
        toast.success("Face names saved for this show");
      }
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save names");
    } finally { setSaving(false); }
  };

  const clearPreferred = async () => {
    setSaving(true);
    try {
      await setPreferred(show.id, { clear: true, apply_all: true });
      toast.success("Back to dynamic auto-follow — re-rendering every clip");
      setPanel(null); setShowVideo(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revert");
    } finally { setSaving(false); }
  };

  const actions = [
    { id: "trim", label: "Trim", icon: Scissors },
    { id: "style", label: "Style", icon: Palette },
    { id: "publish", label: "Publish", icon: Share2 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      data-testid={`clip-card-${clip.id}`}
      className="flex flex-col border border-border bg-card transition-colors hover:border-primary"
    >
      <div className="flex items-start justify-between gap-3 border-b border-border p-4">
        <div className="min-w-0">
          <p className="font-display text-2xl leading-none tracking-wide text-white truncate">{clip.title}</p>
          {sourceLabel && (
            <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-widest text-primary/80">{sourceLabel}</p>
          )}
          <p className="mt-2 font-mono text-[11px] text-muted-foreground">
            {fmt(clip.start_sec)} → {fmt(clip.end_sec)} · {Math.round(clip.duration_sec)}s
            {clip.words?.length > 0 && (
              <span className="ml-2 text-accent"><Sparkles className="mr-1 inline h-3 w-3" />karaoke</span>
            )}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`expand-pad-${clip.id}`}>
            <div className="flex items-center gap-1">
              <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Lead-in</span>
              {[5, 10, 15].map((s) => (
                <button
                  key={`s${s}`}
                  data-testid={`pad-start-${s}-${clip.id}`}
                  onClick={() => pad("start", s)}
                  disabled={saving}
                  className="border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground transition-colors hover:border-accent hover:text-accent disabled:opacity-40"
                >+{s}s</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Runway</span>
              {[5, 10, 15].map((s) => (
                <button
                  key={`e${s}`}
                  data-testid={`pad-end-${s}-${clip.id}`}
                  onClick={() => pad("end", s)}
                  disabled={saving}
                  className="border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground transition-colors hover:border-accent hover:text-accent disabled:opacity-40"
                >+{s}s</button>
              ))}
            </div>
          </div>
          <div className="mt-2.5 flex items-center gap-1.5" data-testid={`rate-${clip.id}`}>
            <span className="mr-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Train:</span>
            <button data-testid={`rate-love-${clip.id}`} onClick={() => rate("love")} title="Love — more like this"
              className={`grid h-7 w-7 place-items-center border transition-colors ${rating === "love" ? "border-accent bg-accent text-black" : "border-border text-muted-foreground hover:border-accent hover:text-accent"}`}>
              <Heart className="h-3.5 w-3.5" fill={rating === "love" ? "currentColor" : "none"} />
            </button>
            <button data-testid={`rate-keep-${clip.id}`} onClick={() => rate("keep")} title="Keep"
              className={`grid h-7 w-7 place-items-center border transition-colors ${rating === "keep" ? "border-accent bg-accent text-black" : "border-border text-muted-foreground hover:border-accent hover:text-accent"}`}>
              <ThumbsUp className="h-3.5 w-3.5" />
            </button>
            <button data-testid={`rate-kill-${clip.id}`} onClick={() => rate("kill")} title="Kill — fewer like this"
              className={`grid h-7 w-7 place-items-center border transition-colors ${rating === "kill" ? "border-primary bg-primary text-white" : "border-border text-muted-foreground hover:border-primary hover:text-primary"}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div
          data-testid={`clip-score-${clip.id}`}
          className={`flex shrink-0 items-center gap-1 px-2.5 py-1 font-mono text-sm font-bold ${scoreColor(clip.funny_score)}`}
        >
          <Flame className="h-3.5 w-3.5" /> {clip.funny_score}
        </div>
      </div>

      <div className="bg-black/40 p-4">
        {clip.generating ? (
          <div data-testid={`clip-generating-${clip.id}`} className="flex items-center justify-center gap-2 border border-dashed border-accent/50 py-8 font-mono text-[11px] uppercase tracking-widest text-accent">
            <Loader2 className="h-4 w-4 animate-spin" /> Cutting this clip…
          </div>
        ) : clip.video_path && showVideo ? (
          <video
            key={clip.video_path}
            data-testid={`clip-video-${clip.id}`}
            src={mediaUrl(clip.video_path)}
            controls playsInline
            className="mx-auto max-h-[420px] w-auto border border-border"
          />
        ) : (
          <div className="space-y-3">
            {clip.video_path && (
              <button
                data-testid={`show-video-${clip.id}`}
                onClick={() => setShowVideo(true)}
                className="flex w-full items-center justify-center gap-2 border border-border bg-secondary/50 py-6 text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:border-accent hover:text-white"
              >
                <Play className="h-4 w-4" /> Load Social Video
              </button>
            )}
            {clip.audio_path && (
              <audio key={clip.audio_path} data-testid={`clip-audio-${clip.id}`} src={mediaUrl(clip.audio_path)} controls className="w-full" />
            )}
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {panel && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-accent/40 bg-accent/[0.03]"
          >
            {panel === "trim" && <WaveformTrimmer clip={clip} show={show} onSave={saveTrim} saving={saving} />}
            {panel === "style" && <StyleControls clip={clip} show={show} onApply={applyStyle} onLock={lockReframe} onNameFaces={nameFaces} onClearPreferred={clearPreferred} saving={saving} isVideo={!!(show?.is_video)} />}
            {panel === "publish" && <PublishHub clip={clip} />}
          </motion.div>
        )}
      </AnimatePresence>

      {clip.reason && (
        <p className="border-b border-border/60 px-4 py-3 text-xs italic text-accent/90">"{clip.reason}"</p>
      )}

      {clip.caption && (
        <div className="px-4 py-3">
          <div className="flex items-start gap-2 text-sm text-white/90">
            <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p>{clip.caption}</p>
          </div>
          {clip.hashtags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {clip.hashtags.map((h, i) => <span key={i} className="font-mono text-[11px] text-accent">{h}</span>)}
            </div>
          )}
        </div>
      )}

      {clip.transcript_text && (
        <p className="mx-4 mb-3 max-h-24 overflow-y-auto border-l-2 border-border pl-3 text-xs leading-relaxed text-muted-foreground">
          {clip.transcript_text}
        </p>
      )}

      <div className="mt-auto grid grid-cols-3 border-t border-border">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.id}
              data-testid={`${a.id}-toggle-${clip.id}`}
              onClick={() => toggle(a.id)}
              className={`flex items-center justify-center gap-1.5 border-r border-border py-3 text-[11px] uppercase tracking-widest transition-colors last:border-r-0 ${
                panel === a.id ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-white"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {a.label}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-3 border-t border-border">
        <button
          data-testid={`copy-caption-${clip.id}`}
          onClick={copyCaption}
          className="flex items-center justify-center gap-1.5 border-r border-border py-3 text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-secondary hover:text-white"
        >
          <Copy className="h-3.5 w-3.5" /> Cap
        </button>
        <button
          data-testid={`download-audio-${clip.id}`}
          onClick={() => download(clip.audio_path, "mp3")}
          disabled={!clip.audio_path}
          className="flex items-center justify-center gap-1.5 border-r border-border py-3 text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-secondary hover:text-white disabled:opacity-30"
        >
          <Download className="h-3.5 w-3.5" /> Audio
        </button>
        <button
          data-testid={`download-video-${clip.id}`}
          onClick={() => download(clip.video_path, "mp4")}
          disabled={!clip.video_path}
          className="flex items-center justify-center gap-1.5 py-3 text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-30"
        >
          <Film className="h-3.5 w-3.5" /> Video
        </button>
      </div>
      {clip.video_path && (
        <button
          data-testid={`share-link-${clip.id}`}
          onClick={() => { navigator.clipboard.writeText(shareUrl(clip.id)); toast.success("Share link copied — anyone can watch it"); }}
          className="flex items-center justify-center gap-2 border-t border-border py-3 text-[11px] uppercase tracking-widest text-accent transition-colors hover:bg-accent hover:text-black"
        >
          <Share2 className="h-3.5 w-3.5" /> Copy share link
        </button>
      )}
      {clip.video_path && ((clip.share_views || 0) > 0 || (clip.share_signups || 0) > 0) && (
        <div data-testid={`share-stats-${clip.id}`} className="flex items-center justify-center gap-4 border-t border-border py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {clip.share_views || 0} views</span>
          <span className="flex items-center gap-1 text-accent"><Sparkles className="h-3 w-3" /> {clip.share_signups || 0} signups</span>
        </div>
      )}
    </motion.div>
  );
};
